import React from 'react';
import { Modal } from 'antd';
import { version } from '../../package.json';
import { resetAppCache } from '../utils/resetAppCache';
global.appVersion = version;

const semverGreaterThan = (versionA, versionB) => {
  const versionsA = versionA.split(/\./g);
  const versionsB = versionB.split(/\./g);

  while (versionsA.length || versionsB.length) {
    const a = Number(versionsA.shift()) || 0;
    const b = Number(versionsB.shift()) || 0;

    if (a === b) continue;
    return a > b;
  }
  return false;
};

const RECHECK_INTERVAL_MS = 30 * 60 * 1000;
const RETRY_AFTER_ERROR_MS = 60 * 1000;
const VISIBILITY_DEBOUNCE_MS = 2 * 60 * 1000;
const FETCH_TIMEOUT_MS = 10 * 1000;

class CacheBuster extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      isLatestVersion: false,
      newVersionAvailable: false,
      latestVersion: null,
      refreshCacheAndReload: async () => {
        if (!navigator.onLine) {
          Modal.warning({
            title: 'Nema interneta',
            content:
              'Osvježavanje zahtijeva internet konekciju kako bi aplikacija mogla preuzeti najnoviju verziju.',
          });
          return;
        }
        await resetAppCache();
      },
    };
    this.checkInFlight = false;
    this.lastCheckAt = 0;
    this.retryTimeoutId = null;
    this.recheckIntervalId = null;
    this.unmounted = false;
  }

  componentDidMount() {
    // Check if we recently refreshed to prevent loops
    const lastRefreshData = localStorage.getItem('lastCacheRefresh');

    if (lastRefreshData) {
      try {
        const { version: lastRefreshVersion, timestamp } =
          JSON.parse(lastRefreshData);
        const timeSinceRefresh = Date.now() - timestamp;
        const currentVersion = global.appVersion;

        // If we refreshed less than 30 seconds ago for the same version, skip
        if (timeSinceRefresh < 30000 && lastRefreshVersion === currentVersion) {
          console.info('Recently refreshed for this version, skipping check');
          this.setState({ loading: false, isLatestVersion: true });
          return;
        }

        // Clean up old refresh data (older than 5 minutes)
        if (timeSinceRefresh > 300000) {
          localStorage.removeItem('lastCacheRefresh');
        }
      } catch (error) {
        // Invalid data, remove it
        localStorage.removeItem('lastCacheRefresh');
      }
    }

    setTimeout(this.checkVersion, 100);

    this.recheckIntervalId = setInterval(
      this.scheduledCheck,
      RECHECK_INTERVAL_MS,
    );
    window.addEventListener('online', this.handleOnline);
    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.retryTimeoutId) clearTimeout(this.retryTimeoutId);
    if (this.recheckIntervalId) clearInterval(this.recheckIntervalId);
    window.removeEventListener('online', this.handleOnline);
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  scheduledCheck = () => {
    if (navigator.onLine) this.checkVersion();
  };

  handleOnline = () => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    this.checkVersion();
  };

  handleVisibility = () => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - this.lastCheckAt < VISIBILITY_DEBOUNCE_MS) return;
    this.checkVersion();
  };

  scheduleRetry = () => {
    if (this.retryTimeoutId) return;
    this.retryTimeoutId = setTimeout(() => {
      this.retryTimeoutId = null;
      if (navigator.onLine) this.checkVersion();
    }, RETRY_AFTER_ERROR_MS);
  };

  checkVersion = () => {
    if (this.checkInFlight || this.unmounted) return;
    this.checkInFlight = true;
    this.lastCheckAt = Date.now();

    const timestamp = Date.now();
    const controller = new AbortController();
    const abortId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    fetch(`/meta.json?v=${timestamp}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((meta) => {
        if (this.unmounted) return;
        const latestVersion = meta.version;
        const changelog = meta.changelog || [];
        const currentVersion = global.appVersion;

        console.info(
          `Server version: ${latestVersion}, App version: ${currentVersion}`,
        );

        const shouldForceRefresh = semverGreaterThan(
          latestVersion,
          currentVersion,
        );

        if (shouldForceRefresh) {
          console.info(
            `We have a new version - ${latestVersion}. Should force refresh`,
          );
          this.setState({
            loading: false,
            isLatestVersion: false,
            newVersionAvailable: true,
            latestVersion,
            changelog,
          });
        } else {
          localStorage.removeItem('lastCacheRefresh');
          this.setState({
            loading: false,
            isLatestVersion: true,
            newVersionAvailable: false,
            latestVersion: null,
            changelog: undefined,
          });
        }
      })
      .catch((err) => {
        // Don't set isLatestVersion=true on failure — that strands users on stale versions.
        const msg = (err && err.message) || '';
        const isAbort =
          (err && err.name === 'AbortError') ||
          /signal is aborted/i.test(msg);
        if (!isAbort) {
          console.warn('[CacheBuster] Failed to check version:', msg);
        }
        if (this.unmounted) return;
        this.setState((prev) => (prev.loading ? { loading: false } : null));
        this.scheduleRetry();
      })
      .finally(() => {
        clearTimeout(abortId);
        this.checkInFlight = false;
      });
  };

  render() {
    const {
      loading,
      isLatestVersion,
      newVersionAvailable,
      latestVersion,
      changelog,
      refreshCacheAndReload,
    } = this.state;
    return this.props.children({
      loading,
      isLatestVersion,
      newVersionAvailable,
      latestVersion,
      changelog,
      currentVersion: global.appVersion,
      refreshCacheAndReload,
    });
  }
}

export default ({ children, manualApprove = false }) => (
  <CacheBuster>
    {(cacheInfo) => {
      if (!manualApprove) {
        if (cacheInfo.loading) return null;
        if (!cacheInfo.isLatestVersion) {
          cacheInfo.refreshCacheAndReload();
          return null;
        }
      }
      return children(cacheInfo);
    }}
  </CacheBuster>
);
